import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { queryKeys } from '@/shared/query';
import { leadAPI, type Lead } from '@/modules/lead/api/lead.api';

const SEARCH_DEBOUNCE_MS = 300;
const SEARCH_LIMIT = 20;
/** Same bar as patient search: no browse list on open. */
export const MIN_LEAD_SEARCH_LENGTH = 2;

const isBookableLead = (lead: Lead): boolean =>
  lead.status !== 'lost' && lead.status !== 'won' && lead.status !== 'unqualified';

const mergeKeepSelected = (
  list: Lead[],
  keep: Lead | null | undefined
): Lead[] => {
  if (!keep?.id) return list;
  if (list.some((l) => l.id === keep.id)) return list;
  return [keep, ...list];
};

/**
 * Debounced lead search for appointment Autocomplete.
 * Does not load a list on open. Searches after the typed term is long enough.
 * Excludes closed leads (lost/won/unqualified); seeded selection is kept.
 * Aborts in-flight search on new input.
 */
export const useAppointmentLeadSearch = (params: {
  selectedLeadId: string;
  seedLeadId?: string | null;
}) => {
  const { selectedLeadId, seedLeadId = null } = params;
  const seedId = seedLeadId || selectedLeadId || null;

  const [options, setOptions] = useState<Lead[]>([]);
  const [searching, setSearching] = useState(false);
  const [queryTooShort, setQueryTooShort] = useState(true);
  /** True after the first search settles. */
  const [listReady, setListReady] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const selectedRef = useRef<Lead | null>(null);

  const seedQuery = useQuery({
    queryKey: queryKeys.lead.detail(seedId ?? ''),
    queryFn: () => leadAPI.getById(seedId!),
    enabled: Boolean(seedId),
  });
  const seededLead = seedQuery.data?.data ?? null;

  useEffect(() => {
    if (!seededLead?.id) return;
    selectedRef.current = seededLead;
    setOptions((prev) => mergeKeepSelected(prev, seededLead));
  }, [seededLead]);

  useEffect(() => {
    if (!selectedLeadId) {
      selectedRef.current = null;
      return;
    }
    const fromOptions = options.find((l) => l.id === selectedLeadId);
    if (fromOptions) {
      selectedRef.current = fromOptions;
    }
  }, [selectedLeadId, options]);

  const clearPendingSearch = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    setSearching(false);
    setOptions((prev) => {
      const kept = mergeKeepSelected([], selectedRef.current);
      if (
        prev.length === kept.length &&
        prev.every((item, index) => item.id === kept[index]?.id)
      ) {
        return prev;
      }
      return kept;
    });
  }, []);

  const runSearch = useCallback(async (term: string) => {
    const query = term.trim();
    if (query.length < MIN_LEAD_SEARCH_LENGTH) {
      clearPendingSearch();
      return;
    }
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setSearching(true);
    try {
      const res = await leadAPI.getAll(
        {
          search: query,
          limit: SEARCH_LIMIT,
        },
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      const list = ((res.data ?? []) as Lead[]).filter(isBookableLead);
      setOptions(mergeKeepSelected(list, selectedRef.current));
    } catch (err) {
      if (axios.isCancel(err) || controller.signal.aborted) return;
      setOptions(mergeKeepSelected([], selectedRef.current));
    } finally {
      if (!controller.signal.aborted) {
        setSearching(false);
        setListReady(true);
      }
    }
  }, [clearPendingSearch]);

  const handleInputChange = useCallback(
    (_event: unknown, value: string, reason: string) => {
      if (reason === 'clear') {
        setQueryTooShort(true);
        clearPendingSearch();
        return;
      }
      if (reason !== 'input') return;
      if (timerRef.current) clearTimeout(timerRef.current);
      const query = value.trim();
      const tooShort = query.length < MIN_LEAD_SEARCH_LENGTH;
      setQueryTooShort(tooShort);
      if (tooShort) {
        clearPendingSearch();
        return;
      }
      setSearching(true);
      timerRef.current = setTimeout(() => {
        void runSearch(query);
      }, SEARCH_DEBOUNCE_MS);
    },
    [clearPendingSearch, runSearch]
  );

  /** Opening the menu does not fetch. Results appear after the user types. */
  const handleOpen = useCallback(() => {}, []);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      abortRef.current?.abort();
    };
  }, []);

  const selectedLead =
    options.find((l) => l.id === selectedLeadId) ??
    (seededLead?.id === selectedLeadId ? seededLead : null) ??
    null;

  return {
    options,
    selectedLead,
    searching,
    queryTooShort,
    listReady,
    seedPending: Boolean(seedId) && seedQuery.isPending,
    seedError: seedQuery.isError,
    handleInputChange,
    handleOpen,
    rememberSelected: (lead: Lead | null) => {
      selectedRef.current = lead;
      if (lead) {
        setOptions((prev) => mergeKeepSelected(prev, lead));
      }
    },
  };
};
